import Tag from "@/components/Tag";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/kit/ui/form";
import { useValibotFormContext } from "@/utils/validation";
import { FormattedMessage } from "react-intl";
import { graphql, useFragment } from "react-relay";
import { TagsFieldsFragment$key } from "./__generated__/TagsFieldsFragment.graphql";
import { Schema } from "./schema";

const Fragment = graphql`
  fragment TagsFieldsFragment on Query
  @argumentDefinitions(activityKind: { type: "ActivityKind!" }) {
    tags(filters: { activityKind: $activityKind }) {
      nodes {
        id
        name
        ...TagFragment
      }
    }
  }
`;

interface Props {
  query: TagsFieldsFragment$key;
}

export function TagsField(props: Props) {
  const form = useValibotFormContext<Schema>();
  const {
    tags: { nodes: tags },
  } = useFragment(Fragment, props.query);

  return (
    <FormField
      control={form.control}
      name="tags"
      render={({ field }) => {
        const selected = field.value ?? [];
        const selectedSet = new Set(selected);
        const toggleTag = (tag: string) => {
          const current = field.value ?? [];

          const next = current.includes(tag)
            ? current.filter((name) => name !== tag)
            : [...current, tag];

          field.onChange(next);
        };

        return (
          <FormItem>
            <FormLabel className="text-sm font-medium text-grey-600">
              <FormattedMessage defaultMessage="Categories" />
            </FormLabel>
            <FormControl className="pt-4">
              {!tags.length ? (
                <FormDescription>
                  <FormattedMessage defaultMessage="No categories available yet." />
                </FormDescription>
              ) : (
                <div className="flex flex-wrap gap-2 overflow-y-auto">
                  {tags.map((node) => {
                    const isActive = selectedSet.has(node.name);
                    return (
                      <Tag
                        key={node.id}
                        tag={node}
                        variant={isActive ? "secondary" : undefined}
                        className="hover:cursor-pointer select-none"
                        onClick={(event) => {
                          event.preventDefault();
                          toggleTag(node.name);
                        }}
                        onRemove={
                          isActive ? () => toggleTag(node.name) : undefined
                        }
                      />
                    );
                  })}
                </div>
              )}
            </FormControl>
          </FormItem>
        );
      }}
    />
  );
}
